import currency from "currency.js";
import { forwardRef } from "react";
import { OptionTypeResponse, SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { formatSizePrecision } from "../../../utils/format";
import { Cell, OptionRow, OptionsRowWrapper } from "../style";
import { StrikeColumn } from "../../shared/Table/style";
import BidAskSizeCell from "./Cells/BidAskSizeCell";
import PositionCell from "./Cells/PositionCell";
import { useOptionsChainTableHeaders } from "./headers";
import { IOptionVolume, ITableContentRowWithCallPut } from "./types";

interface INeedsAnimation {
  call: {
    bid: boolean;
    ask: boolean;
  };
  put: {
    bid: boolean;
    ask: boolean;
  };
}

interface IRowBothProps {
  row: ITableContentRowWithCallPut;
  onTrade: (
    orderType: OptionTypeResponse,
    expiry: number,
    strike: string
  ) => void;
  totalCallsPutsSize: IOptionVolume;
  sideTableWidth: string;
  strikeWidth: number;
  decimals: number;
  activeCellType: OptionTypeResponse | undefined;
  needsAnimation: INeedsAnimation;
  computeIV: (type: OptionTypeResponse, side: SideResponse) => string;
  bidHeaders: ReturnType<typeof useOptionsChainTableHeaders>["bidHeaders"];
  headers: ReturnType<typeof useOptionsChainTableHeaders>["headers"];
}

const RowBoth = forwardRef<any, IRowBothProps>(
  (
    {
      row,
      onTrade,
      totalCallsPutsSize,
      sideTableWidth,
      strikeWidth,
      decimals,
      activeCellType,
      needsAnimation,
      computeIV,
      bidHeaders,
      headers,
    },
    ref
  ) => {
    const callOI = Number(row.call.open_interest)
      ? formatSizePrecision(row.call.open_interest, decimals)
      : "-";
    const putOI = Number(row.put.open_interest)
      ? formatSizePrecision(row.put.open_interest, decimals)
      : "-";
    return (
      <OptionsRowWrapper ref={ref}>
        <>
          <OptionRow
            disabled={!row.call.instrumentName}
            $width={sideTableWidth}
            onClick={
              row.call.instrumentName
                ? () =>
                    onTrade(OptionTypeResponse.Call, row.expiry, row.strike)
                : undefined
            }
            $isActive={activeCellType === OptionTypeResponse.Call}
            side={"left"}
          >
            <BidAskSizeCell
              orderVolume={row.call.bid.orderVolume}
              totalVolume={totalCallsPutsSize.calls.bids}
              volume={Number(row.call.bid.volume)}
              needsAnimation={needsAnimation.call.bid}
              side={SideResponse.Buy}
              align={bidHeaders.bidSize.align}
            />
            {bidHeaders.priceIvBid && (
              <Cell dim align={bidHeaders.priceIvBid.align}>
                <Cell
                  style={{ color: COLORS.up.one }}
                  align={bidHeaders.priceIvBid.align}
                >
                  {row.call.bid.price && Number(row.call.bid.price)
                    ? currency(row.call.bid.price).format()
                    : "-"}
                </Cell>
                {computeIV(OptionTypeResponse.Call, SideResponse.Buy)}
              </Cell>
            )}
            {bidHeaders.markPrice && (
              <Cell>
                <Cell align={bidHeaders.markPrice.align}>
                  {Number(row.call.markPrice) > 0.01
                    ? currency(Number(row.call.markPrice)).format({
                        precision: 2,
                      })
                    : "-"}
                </Cell>
                <Cell dim align={bidHeaders.markPrice.align} $fontSize="small">
                  {Number(row.call.markPrice) > 0.01
                    ? `${(Number(row.call.iv) * 100).toFixed(2)}%`
                    : "-"}
                </Cell>
              </Cell>
            )}
            {bidHeaders.priceIvAsk && (
              <Cell dim align={bidHeaders.priceIvAsk.align}>
                <Cell
                  style={{ color: COLORS.down.one }}
                  align={bidHeaders.priceIvAsk.align}
                >
                  {row.call.ask.price && Number(row.call.ask.price)
                    ? currency(row.call.ask.price).format()
                    : "-"}
                </Cell>
                {computeIV(OptionTypeResponse.Call, SideResponse.Sell)}
              </Cell>
            )}
            <BidAskSizeCell
              orderVolume={row.call.ask.orderVolume}
              totalVolume={totalCallsPutsSize.calls.asks}
              volume={Number(row.call.ask.volume)}
              needsAnimation={needsAnimation.call.ask}
              side={SideResponse.Sell}
              align={bidHeaders.askSize.align}
            />
            {headers.delta && (
              <Cell dim align={headers.delta.align}>
                {row.call.delta && Number(row.call.delta).toFixed(2)}
              </Cell>
            )}
            {headers.oi && (
              <Cell dim align={headers.oi.align}>
                {callOI}
              </Cell>
            )}
            {headers.position && (
              <PositionCell
                align={headers.position.align}
                position={row.call.position}
                side={row.call.side}
              />
            )}
          </OptionRow>
          <StrikeColumn
            $width={strikeWidth}
            activeBorderSide={
              activeCellType &&
              (activeCellType === OptionTypeResponse.Call ? "right" : "left")
            }
          >
            <span>
              {row.strike
                ? currency(row.strike, { precision: 0 }).format()
                : "-"}
            </span>
          </StrikeColumn>
          <OptionRow
            disabled={!row.put.instrumentName}
            $width={sideTableWidth}
            onClick={
              row.put.instrumentName
                ? () =>
                    onTrade(OptionTypeResponse.Put, row.expiry, row.strike)
                : undefined
            }
            $isActive={activeCellType === OptionTypeResponse.Put}
            side={"right"}
          >
            {headers.position && (
              <PositionCell
                align={headers.position.align}
                position={row.put.position}
                side={row.put.side}
              />
            )}
            {headers.oi && (
              <Cell dim align={headers.oi.align}>
                {putOI}
              </Cell>
            )}
            {headers.delta && (
              <Cell dim align={headers.delta.align}>
                {row.put.delta && Number(row.put.delta).toFixed(2)}
              </Cell>
            )}
            <BidAskSizeCell
              orderVolume={row.put.bid.orderVolume}
              totalVolume={totalCallsPutsSize.puts.bids}
              volume={Number(row.put.bid.volume)}
              needsAnimation={needsAnimation.put.bid}
              side={SideResponse.Buy}
              align={bidHeaders.bidSize.align}
            />
            {bidHeaders.priceIvBid && (
              <Cell dim align={bidHeaders.priceIvBid.align}>
                <Cell
                  style={{ color: COLORS.up.one }}
                  align={bidHeaders.priceIvBid.align}
                >
                  {row.put.bid.price && Number(row.put.bid.price)
                    ? currency(row.put.bid.price).format()
                    : "-"}
                </Cell>
                {computeIV(OptionTypeResponse.Put, SideResponse.Buy)}
              </Cell>
            )}
            {bidHeaders.markPrice && (
              <Cell>
                <Cell align={bidHeaders.markPrice.align}>
                  {Number(row.put.markPrice) > 0
                    ? currency(Number(row.put.markPrice)).format({
                        precision: 2,
                      })
                    : "-"}
                </Cell>
                <Cell dim align={bidHeaders.markPrice.align} $fontSize="small">
                  {Number(row.put.iv) > 0
                    ? `${(Number(row.put.iv) * 100).toFixed(2)}%`
                    : "-"}
                </Cell>
              </Cell>
            )}
            {bidHeaders.priceIvAsk && (
              <Cell dim align={bidHeaders.priceIvAsk.align}>
                <Cell
                  style={{ color: COLORS.down.one }}
                  align={bidHeaders.priceIvAsk.align}
                >
                  {row.put.ask.price && Number(row.put.ask.price)
                    ? currency(row.put.ask.price).format()
                    : "-"}
                </Cell>
                {computeIV(OptionTypeResponse.Put, SideResponse.Sell)}
              </Cell>
            )}
            <BidAskSizeCell
              orderVolume={row.put.ask.orderVolume}
              totalVolume={totalCallsPutsSize.puts.asks}
              volume={Number(row.put.ask.volume)}
              needsAnimation={needsAnimation.put.ask}
              side={SideResponse.Sell}
              align={bidHeaders.askSize.align}
            />
          </OptionRow>
        </>
      </OptionsRowWrapper>
    );
  }
);

export default RowBoth;
